/* eslint-disable @typescript-eslint/no-explicit-any */
// src/components/group-order/GroupOrderDetailsSection.tsx
import { Box, Flex, HStack, Stack, Text, VStack, Image, IconButton } from '@chakra-ui/react';
import DummyImage from '@/assets/dummy.png';
import { getCurrencySymbol } from '@/utils/currencyUtils';
import StatusBadge from '@/components/common/StatusBadge';
import { convertDateFormat } from '@/utils/dateConverter';
import Button from '@/components/common/Button';
import CustomConfirmationModal from '@/components/common/ConfirmationModal';
import CheckIcon from '@/assets/svg/check-stroke.svg?react';
import { useQueryClient } from 'react-query';
import {
  useGetMaintenanceImagesByBillId,
  useMoveStatus,
  useUploadMaintenanceImage,
} from '@/services/OrderManagement';
import { useState } from 'react';
import CustomizedFieldsTooltip from './CustomizedFieldsTooltip';
import ImageWithBadge from '@/components/common/ImageWithBadge';
import CustomizationIcon from '@/assets/svg/customizable-icon.svg';
import AddOnsTooltip from './AddonsTooltip';
import AddOnsIcon from '@/assets/svg/add-ons-icon.svg';
import useCustomToast from '@/components/common/Toast';
import MaintenanceConfirmationModal from '@/components/Orders/MaintenanceConfirmationModal';
import ImageIcon from '../../../assets/svg/image_upload.svg?react';
import MaintenanceImageModal from '@/components/Orders/MaintenanceImageModal';

interface GroupOrderDetailsSectionProps {
  order: any;
  totalAmount: number;
}

export const GroupOrderDetailsSection: React.FC<GroupOrderDetailsSectionProps> = ({
  order,
  totalAmount,
}) => {
  const queryClient = useQueryClient();
  const { mutateAsync: moveStatus, isLoading: isMovingStatus } = useMoveStatus();
  const { mutateAsync: uploadImage, isLoading: isUploadingImage } = useUploadMaintenanceImage();
  const { showToast } = useCustomToast();

  const [isSimpleModalOpen, setIsSimpleModalOpen] = useState(false);
  const [isRichModalOpen, setIsRichModalOpen] = useState(false);

  const [pendingMove, setPendingMove] = useState<{
    orderId: string;
    orderItemId: number;
    currentStatus: string;
    isCustomized: boolean;
    nextStatus: string;
  } | null>(null);

  // Rich modal state
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [, setImagePreview] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [, setShowImageError] = useState(false);

  const openConfirmModal = (
    orderId: string,
    orderItemId: number,
    currentStatus: string,
    isCustomized: boolean,
  ) => {
    let nextStatus = '';

    if (currentStatus === 'Order Confirmed') {
      nextStatus = isCustomized ? 'Under Maintainance' : 'Ready for Delivery';
    } else if (currentStatus === 'Under Maintainance') {
      nextStatus = 'Ready for Delivery';
    } else {
      return;
    }

    setPendingMove({
      orderId,
      orderItemId,
      currentStatus,
      isCustomized,
      nextStatus,
    });

    // Determine if image upload is required
    const needsImageUpload =
      currentStatus === 'Under Maintainance' ||
      (currentStatus === 'Order Confirmed' && !isCustomized);

    if (needsImageUpload) {
      setUploadedFile(null);
      setImagePreview(null);
      setComment('');
      setShowImageError(false);
      setIsRichModalOpen(true);
    } else {
      setIsSimpleModalOpen(true);
    }
  };

  const closeModals = () => {
    setIsSimpleModalOpen(false);
    setIsRichModalOpen(false);
    setPendingMove(null);
    setUploadedFile(null);
    setImagePreview(null);
    setComment('');
    setShowImageError(false);
  };

  const billId = order.bill_id;

  // Fetch all maintenance images for the entire transaction (group order)
  const { data: allMaintenanceImages = [], isLoading: imagesLoading } =
    useGetMaintenanceImagesByBillId(billId || '', {
      enabled: !!billId,
    });

  // State to track which item's images to show
  const [selectedOrderItemPk, setSelectedOrderItemPk] = useState<number | null>(null);

  // Get images for the currently selected item
  const currentItemImages = selectedOrderItemPk
    ? allMaintenanceImages.filter((img: any) => img.order_item === selectedOrderItemPk)
    : [];

  // Check if a specific item has images
  const itemHasImages = (orderItemPk: number) => {
    return allMaintenanceImages.some((img: any) => img.order_item === orderItemPk);
  };

  const handleConfirmMove = async () => {
    if (!pendingMove) return;

    const needsImageUpload =
      pendingMove.currentStatus === 'Under Maintainance' ||
      (pendingMove.currentStatus === 'Order Confirmed' && !pendingMove.isCustomized);

    if (needsImageUpload && !uploadedFile) {
      setShowImageError(true);
      showToast('error', 'Please upload a ORDER COMPLETION IMAGE.');
      return;
    }

    setShowImageError(false);

    try {
      // Upload image first if required
      if (needsImageUpload && uploadedFile) {
        const formData = new FormData();
        formData.append('image', uploadedFile);
        formData.append('order_item_id', String(pendingMove.orderItemId));
        if (comment.trim()) {
          formData.append('comment', comment.trim());
        }

        await uploadImage(formData);
      }

      // Then move status
      const response = await moveStatus({
        orderId: pendingMove.orderId,
        newStatus: pendingMove.nextStatus,
      });

      showToast('success', response?.message || `Status moved to "${pendingMove.nextStatus}"`);

      queryClient.invalidateQueries(['groupOrder', order.bill_id || order.id]);
      closeModals();
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        error?.message ||
        'Failed to complete the process';
      showToast('error', message);
    }
  };

  return (
    <>
      <Box border="1px solid" borderColor="border.default" borderRadius="lg" overflow="hidden">
        {/* Header */}
        <Flex
          direction={{ base: 'column', md: 'row' }}
          justifyContent="space-between"
          py={3}
          px={4}
          bg="background.tertiary"
          fontFamily="primary"
          gap={4}
        >
          <HStack spacing={6} flexWrap="wrap">
            <Stack direction="column" gap={0}>
              <Text color="text.secondary" fontSize="xs">
                Transaction Id
              </Text>
              <Text color="text.primary" fontSize="xs">
                {order.transaction_id}
              </Text>
            </Stack>
            <Stack direction="column" gap={0}>
              <Text color="text.secondary" fontSize="xs">
                Bill ID
              </Text>
              <Text color="text.primary" fontSize="xs">
                {order.bill_id}
              </Text>
            </Stack>
          </HStack>

          <HStack spacing={6} flexWrap="wrap" justify={{ base: 'flex-start', md: 'flex-end' }}>
            <Stack direction="column" gap={0}>
              <Text color="text.secondary" fontSize="xs">
                Order Placed
              </Text>
              <Text color="text.primary" fontSize="xs">
                {convertDateFormat(order.created_at)}
              </Text>
            </Stack>
            <Stack direction="column" gap={0}>
              <Text color="text.secondary" fontSize="xs">
                Total
              </Text>
              <Text color="text.primary" fontSize="xs">
                {getCurrencySymbol(order.received_currency || order.currency || 'USD')}
                {totalAmount.toFixed(2)}
              </Text>
            </Stack>
            <Stack direction="column" gap={0}>
              <Text color="text.secondary" fontSize="xs">
                Ship To
              </Text>
              <Text color="text.primary" fontSize="xs">
                {order.shipment_address?.name || 'N/A'}
              </Text>
            </Stack>
          </HStack>
        </Flex>

        {/* Body */}
        <Box p={4} bg="background.white">
          <Stack spacing={6} divider={<Box borderTop="1px solid" borderColor="border.default" />}>
            {order.purchased_items?.map((item: any, index: number) => {
              const cart = item.cart_item;
              const txn = item.order_tracking.individual_txn;
              const tracking = item.order_tracking;
              const orderId = tracking?.order_id || item?.order_id;
              const currentStatus = tracking?.order_status?.status_name || 'Processing';
              const isCustomized = cart?.is_customized || false;

              const canMoveStatus =
                currentStatus === 'Order Confirmed' || currentStatus === 'Under Maintainance';

              return (
                <Flex
                  key={item.id || index}
                  direction={{ base: 'column', md: 'row' }}
                  align={{ base: 'flex-start', md: 'center' }}
                  gap={{ base: 4, md: 6 }}
                >
                  <HStack
                    spacing={4}
                    flex="1"
                    minW="0"
                    align={{ base: 'stretch', md: 'center' }}
                    flexDirection={{ base: 'column', md: 'row' }}
                  >
                    <ImageWithBadge
                      src={cart?.product_image || DummyImage}
                      w={{ base: '70px', md: '80px' }}
                      h={{ base: '70px', md: '80px' }}
                      quantity={cart?.quantity || 1}
                    />

                    <VStack align="start" spacing={1} flex="1">
                      <Text fontWeight="medium" fontSize={{ base: 'sm', md: 'md' }} noOfLines={2}>
                        {cart?.product_name || 'Unknown Product'}
                      </Text>
                      <HStack
                        fontSize="xs"
                        color="text.secondary"
                        spacing={1}
                        align="center"
                        wrap="wrap"
                      >
                        {cart?.is_customized ? (
                          <>
                            <CustomizedFieldsTooltip
                              customizedFields={cart?.customized_fields}
                              category={cart?.category_name || ''}
                            >
                              <Box
                                as="span"
                                display="inline-flex"
                                alignItems="center"
                                justifyContent="center"
                                w="20px"
                                h="20px"
                                cursor="pointer"
                                title="Customized"
                                bg="background.tertiary"
                                borderRadius="sm"
                              >
                                <Image
                                  src={CustomizationIcon}
                                  alt="Customized"
                                  w="16px"
                                  h="16px"
                                  objectFit="contain"
                                />
                              </Box>
                            </CustomizedFieldsTooltip>

                            {cart?.customized_fields?.ProductAddOn_ids?.length > 0 && (
                              <AddOnsTooltip addOns={cart?.customized_fields?.add_ons || []}>
                                <Box
                                  as="span"
                                  display="inline-flex"
                                  alignItems="center"
                                  justifyContent="center"
                                  w="20px"
                                  h="20px"
                                  cursor="pointer"
                                  title="Add-ons"
                                  bg="background.tertiary"
                                  borderRadius="sm"
                                >
                                  <Image
                                    src={AddOnsIcon}
                                    alt="Add-ons"
                                    w="20px"
                                    h="20px"
                                    objectFit="contain"
                                  />
                                </Box>
                              </AddOnsTooltip>
                            )}
                          </>
                        ) : (
                          <Text as="span">{cart?.variant_size || 'N/A'}</Text>
                        )}
                      </HStack>
                    </VStack>

                    {orderId && canMoveStatus && (
                      <Button
                        variant="primary"
                        size="sm"
                        width={{ base: '100%', md: 'auto' }}
                        minW={{ md: '140px' }}
                        onClick={() =>
                          openConfirmModal(orderId, tracking.pk, currentStatus, isCustomized)
                        }
                        borderRadius="xl"
                        px={6}
                        alignSelf={{ base: 'stretch', md: 'center' }}
                      >
                        Move Status
                      </Button>
                    )}
                  </HStack>

                  <HStack
                    spacing={6}
                    justify={{ base: 'space-between', md: 'flex-end' }}
                    width={{ base: '100%', md: 'auto' }}
                    minW={{ md: '200px' }}
                  >
                    {orderId && (
                      <IconButton
                        aria-label="View maintenance images"
                        icon={<ImageIcon color="#FEA01A" />}
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        onClick={() => setSelectedOrderItemPk(tracking.pk)}
                        isLoading={imagesLoading}
                        visibility={itemHasImages(tracking.pk) ? 'visible' : 'hidden'}
                        isDisabled={!itemHasImages(tracking.pk)}
                      />
                    )}
                    <StatusBadge status={currentStatus} />
                    <Text
                      fontWeight="semibold"
                      fontSize={{ base: 'sm', md: 'md' }}
                      textAlign="right"
                    >
                      {getCurrencySymbol(order.currency || 'USD')}
                      {(
                        (parseFloat(txn?.unit_price || '0') +
                          parseFloat(txn?.customized_price || '0') +
                          parseFloat(txn?.add_ons_price || '0')) *
                        (cart?.quantity || 1)
                      ).toFixed(2)}
                    </Text>
                  </HStack>
                </Flex>
              );
            })}
          </Stack>
        </Box>
      </Box>

      <MaintenanceImageModal
        isOpen={!!selectedOrderItemPk}
        onClose={() => setSelectedOrderItemPk(null)}
        images={currentItemImages}
        isLoading={imagesLoading}
      />

      {/* Simple Confirmation Modal (Customized → Under Maintenance) */}
      <CustomConfirmationModal
        isOpen={isSimpleModalOpen}
        onClose={closeModals}
        onConfirm={handleConfirmMove}
        icon={CheckIcon}
        title="Confirm Status Change"
        description={
          pendingMove
            ? `Are you sure you want to move this item to "${pendingMove.nextStatus}"?`
            : ''
        }
        buttonText="Confirm"
        isLoading={isMovingStatus}
      />

      <MaintenanceConfirmationModal
        isOpen={isRichModalOpen}
        onClose={closeModals}
        onConfirm={async (file, comment) => {
          if (!pendingMove) return;

          const formData = new FormData();
          formData.append('image', file);
          formData.append('order_item_id', String(pendingMove.orderItemId));
          if (comment.trim()) {
            formData.append('comment', comment.trim());
          }

          await uploadImage(formData);

          const response = await moveStatus({
            orderId: pendingMove.orderId,
            newStatus: pendingMove.nextStatus,
          });

          showToast('success', response?.message || `Status moved to "${pendingMove.nextStatus}"`);
          queryClient.invalidateQueries(['groupOrder', order.bill_id || order.id]);
        }}
        description={`Are you sure you want to move this item to "${pendingMove?.nextStatus}"?`}
        imageLabel="Upload ORDER COMPLETION IMAGE"
        targetStatus={pendingMove?.nextStatus || ''}
        isLoading={isUploadingImage || isMovingStatus}
      />
    </>
  );
};
